import { useEffect, useState, useMemo } from 'react';
import { GetMonitors } from '../common/uptimerobot';
import { formatNumber } from '../common/helper';

function OverallStatus({ apiConfigs }) {
    const { CountDays } = window.Config;
    const [allMonitors, setAllMonitors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const results = await Promise.all(
                    apiConfigs.map((config) => GetMonitors(config.key, CountDays))
                );
                // Flatten array of arrays
                const monitors = results.flat();
                setAllMonitors(monitors);
            } catch (error) {
                console.error('Failed to fetch monitors:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [apiConfigs, CountDays]);

    const stats = useMemo(() => {
        if (allMonitors.length === 0) return null;

        const totalMonitors = allMonitors.length;
        const operationalCount = allMonitors.filter((m) => m.status === 'ok').length;
        const downCount = allMonitors.filter((m) => m.status === 'down').length;

        // Calculate overall average uptime
        const totalUptime = allMonitors.reduce((sum, m) => sum + parseFloat(m.average), 0);
        const averageUptime = formatNumber(totalUptime / totalMonitors);

        // Calculate total outages and duration
        const totalOutages = allMonitors.reduce((sum, m) => sum + m.total.times, 0);
        const totalDownDuration = allMonitors.reduce((sum, m) => sum + m.total.duration, 0);

        // Determine overall status
        let overallStatus = 'ok';
        if (downCount > 0) overallStatus = 'down';
        else if (operationalCount < totalMonitors) overallStatus = 'partial';

        return {
            totalMonitors,
            operationalCount,
            downCount,
            averageUptime,
            totalOutages,
            totalDownDuration,
            overallStatus,
        };
    }, [allMonitors]);

    const formatDuration = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
        return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`;
    };

    const statusLabels = {
        ok: 'All Systems Operational',
        partial: 'Partial Outage',
        down: 'Major Outage',
    };

    if (loading) {
        return (
            <div id="overall-status" className="loading-state">
                <div className="loading" />
            </div>
        );
    }

    if (!stats) return null;

    return (
        <div id="overall-status" className={stats.overallStatus}>
            <div className="status-header">
                <span className="status-icon" />
                <span className="status-text">{statusLabels[stats.overallStatus]}</span>
            </div>
            <div className="stats-grid">
                <div className="stat-item">
                    <span className="stat-value">{stats.averageUptime}%</span>
                    <span className="stat-label">Avg Uptime ({CountDays}d)</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.operationalCount}/{stats.totalMonitors}</span>
                    <span className="stat-label">Services Up</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.totalOutages}</span>
                    <span className="stat-label">Total Outages</span>
                </div>
                <div className="stat-item">
                    <span className="stat-value">{stats.totalDownDuration > 0 ? formatDuration(stats.totalDownDuration) : '0s'}</span>
                    <span className="stat-label">Total Downtime</span>
                </div>
            </div>
        </div>
    );
}

export default OverallStatus;
